import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  DirectionsWalk,
  Timeline,
  TrendingUp,
  EmojiEvents
} from '@mui/icons-material';

const StatsCards = ({ stats, todayStats, loading }) => {
  const getStepProgress = () => {
    if (!todayStats || !todayStats.goal) return 0;
    return Math.min((todayStats.steps / todayStats.goal) * 100, 100);
  };

  const formatNumber = (value) => {
    if (typeof value !== 'number') return '0';
    return value.toLocaleString();
  };

  const statItems = [
    {
      title: 'Today\'s Steps',
      value: todayStats?.steps || 0,
      target: todayStats?.goal || 10000,
      icon: <DirectionsWalk />,
      color: '#4CAF50',
      showProgress: true,
      subtitle: `Goal: ${formatNumber(todayStats?.goal || 10000)}`
    },
    {
      title: 'Weekly Average',
      value: stats?.weeklyAverage || 0,
      icon: <Timeline />,
      color: '#2196F3',
      subtitle: 'Steps per day'
    },
    {
      title: 'This Week Total',
      value: stats?.weeklyTotal || 0,
      icon: <TrendingUp />,
      color: '#FF9800',
      subtitle: 'Steps this week'
    },
    {
      title: 'Achievements',
      value: stats?.totalAchievements || 0,
      icon: <EmojiEvents />,
      color: '#F44336',
      subtitle: 'Milestones reached'
    }
  ];

  return (
    <Grid container spacing={3} sx={{ width: '100%' }}>
      {statItems.map((item, index) => (
        <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                <Box
                  sx={{
                    backgroundColor: `${item.color}20`,
                    borderRadius: 2,
                    p: 1,
                    color: item.color
                  }}
                >
                  {item.icon}
                </Box>
                {item.title === 'Today\'s Steps' && todayStats?.lastSync && (
                  <Chip 
                    label="Synced" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                  />
                )}
              </Box>
              
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {loading ? (
                  <Skeleton width={80} />
                ) : (
                  item.formatter ? item.formatter(item.value) : formatNumber(item.value)
                )}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {item.title}
              </Typography>
              
              {item.subtitle && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {item.subtitle}
                </Typography>
              )}

              {item.showProgress && !loading && (
                <Box mt={1}>
                  <LinearProgress 
                    variant="determinate" 
                    value={getStepProgress()}
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      backgroundColor: `${item.color}20`,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: item.color
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {getStepProgress().toFixed(0)}% of daily goal
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsCards;